<template>
  <VDialog v-model="showDialog" max-width="800">
    <VCard v-if="customer">
      <VCardTitle class="d-flex align-center">
        <VAvatar :color="getTierColor(customer)" class="mr-3" size="50">
          <VIcon class="mdi">{{ getTierIcon(customer) }}</VIcon>
        </VAvatar>
        <div>
          <div>{{ customer.firstName || customer.name }} {{ customer.lastName || '' }}</div>
          <div class="text-caption text-medium-emphasis">{{ getTierLabel(customer) }} Member</div>
        </div>
        <VSpacer/>
        <VBtn icon="mdi mdi-close" variant="text" @click="showDialog = false"/>
      </VCardTitle>

      <VTabs v-model="activeTab" color="primary">
        <VTab value="overview">Overview</VTab>
        <VTab value="orders">Orders</VTab>
        <VTab value="visits">Visit History</VTab>
        <VTab value="facial">Facial Recognition</VTab>
      </VTabs>

      <VDivider/>

      <VCardText>
        <VWindow v-model="activeTab">
          <VWindowItem value="overview">
            <VRow>
              <VCol cols="12" md="6">
                <div class="text-h6 mb-3">Contact Information</div>
                <VList density="compact">
                  <VListItem>
                    <template #prepend>
                      <VIcon class="mdi">mdi mdi-email</VIcon>
                    </template>
                    <VListItemTitle>{{ customer.email || 'No email' }}</VListItemTitle>
                  </VListItem>
                  <VListItem>
                    <template #prepend>
                      <VIcon class="mdi">mdi mdi-phone</VIcon>
                    </template>
                    <VListItemTitle>{{ customer.phone || 'No phone' }}</VListItemTitle>
                  </VListItem>
                </VList>
              </VCol>

              <VCol cols="12" md="6">
                <div class="text-h6 mb-3">Statistics</div>
                <VList density="compact">
                  <VListItem>
                    <template #prepend>
                      <VIcon class="mdi">mdi mdi-star</VIcon>
                    </template>
                    <VListItemTitle>{{ customer.loyaltyPoints || 0 }} Loyalty Points</VListItemTitle>
                  </VListItem>
                  <VListItem>
                    <template #prepend>
                      <VIcon class="mdi">mdi mdi-cart</VIcon>
                    </template>
                    <VListItemTitle>{{ customer.totalOrders || 0 }} Total Orders</VListItemTitle>
                  </VListItem>
                  <VListItem>
                    <template #prepend>
                      <VIcon class="mdi">mdi mdi-currency-usd</VIcon>
                    </template>
                    <VListItemTitle>${{ Number(customer.totalSpend || 0).toFixed(2) }} Total Spend</VListItemTitle>
                  </VListItem>
                </VList>
              </VCol>
            </VRow>
          </VWindowItem>

          <VWindowItem value="orders">
            <div class="text-h6 mb-3">Recent Orders</div>
            <VProgressLinear v-if="loadingDetails" color="primary" indeterminate/>
            <VList v-else-if="recentOrders.length > 0">
              <VListItem
                v-for="order in recentOrders"
                :key="order.id"
              >
                <VListItemTitle>#{{ order.id }} - ${{ Number(order.totalAmount).toFixed(2) }}</VListItemTitle>
                <VListItemSubtitle>{{ formatDate(order.placedAt) }} - {{ order.status }}</VListItemSubtitle>
              </VListItem>
            </VList>
            <VAlert v-else color="info" variant="tonal">
              No recent orders
            </VAlert>
          </VWindowItem>

          <VWindowItem value="visits">
            <div class="d-flex align-center justify-space-between mb-3">
              <div class="text-h6">Visit Timeline</div>
              <VChip v-if="visitHistory.length > 0" color="primary" size="small">
                {{ visitHistory.length }} visits
              </VChip>
            </div>
            <VProgressLinear v-if="loadingVisits" color="purple" indeterminate/>
            <VTimeline v-else-if="visitHistory.length > 0" align="start" density="compact" side="end">
              <VTimelineItem
                v-for="(visit, index) in visitHistory"
                :key="index"
                :dot-color="visit.hadOrder ? 'success' : 'grey'"
                size="small"
              >
                <template #opposite>
                  <div class="text-caption text-medium-emphasis">
                    {{ formatVisitDate(visit.visitedAt) }}
                  </div>
                </template>
                <VCard density="compact" variant="tonal">
                  <VCardText class="pa-2">
                    <div class="d-flex align-center">
                      <VIcon
                        :color="visit.hadOrder ? 'success' : 'grey'"
                        class="mr-2"
                        size="small"
                      >
                        {{ visit.hadOrder ? 'mdi mdi-cart-check' : 'mdi mdi-walk' }}
                      </VIcon>
                      <div>
                        <div class="text-body-2">
                          {{ visit.hadOrder ? `Order #${visit.orderId}` : 'Visit only' }}
                        </div>
                        <div v-if="visit.totalAmount" class="text-caption text-success">
                          ${{ Number(visit.totalAmount).toFixed(2) }}
                        </div>
                      </div>
                    </div>
                  </VCardText>
                </VCard>
              </VTimelineItem>
            </VTimeline>
            <VAlert v-else color="info" variant="tonal">
              <template #prepend>
                <VIcon>mdi mdi-face-recognition</VIcon>
              </template>
              No visit history recorded. Visit history is tracked via Patronect face recognition.
            </VAlert>
          </VWindowItem>

          <VWindowItem value="facial">
            <div class="text-h6 mb-3">Facial Recognition Consent</div>

            <VAlert
              :color="customer.facialRecognitionConsent ? 'success' : 'warning'"
              class="mb-4"
              variant="tonal"
            >
              <template #prepend>
                <VIcon>{{ customer.facialRecognitionConsent ? 'mdi mdi-check-circle' : 'mdi mdi-alert-circle' }}</VIcon>
              </template>
              <div class="font-weight-medium">
                {{ customer.facialRecognitionConsent ? 'Consent Granted' : 'Consent Not Granted' }}
              </div>
              <div v-if="customer.facialRecognitionConsentAt" class="text-caption">
                Granted on {{ formatDate(customer.facialRecognitionConsentAt) }}
                <span v-if="customer.facialRecognitionConsentMethod"> via {{ customer.facialRecognitionConsentMethod }}</span>
              </div>
              <div v-if="customer.facialRecognitionConsentWithdrawnAt" class="text-caption text-error">
                Withdrawn on {{ formatDate(customer.facialRecognitionConsentWithdrawnAt) }}
              </div>
            </VAlert>

            <VCard v-if="!customer.facialRecognitionConsent" variant="outlined" class="mb-4">
              <VCardText>
                <div class="text-body-2 mb-3">
                  To enable facial recognition for this customer, they must provide explicit consent.
                  Please read the following to the customer:
                </div>
                <VAlert color="info" variant="tonal" class="mb-3">
                  <div class="text-body-2" style="white-space: pre-line;">{{ consentText }}</div>
                </VAlert>
                <VCheckbox
                  v-model="consentAcknowledged"
                  label="Customer verbally agrees to the above terms"
                  density="compact"
                />
              </VCardText>
              <VCardActions>
                <VSpacer />
                <VBtn
                  :disabled="!consentAcknowledged"
                  :loading="grantingConsent"
                  color="success"
                  variant="flat"
                  @click="grantConsent"
                >
                  <VIcon start>mdi mdi-check</VIcon>
                  Record Consent
                </VBtn>
              </VCardActions>
            </VCard>

            <VCard v-else variant="outlined" class="mb-4">
              <VCardText>
                <div class="d-flex align-center justify-space-between mb-3">
                  <div>
                    <div class="font-weight-medium">Linked Faces</div>
                    <div class="text-caption text-disabled">Faces linked to this customer's account</div>
                  </div>
                  <VBtn
                    :loading="linkingFace"
                    color="primary"
                    variant="tonal"
                    size="small"
                    @click="startFaceLinking"
                  >
                    <VIcon start>mdi mdi-camera-plus</VIcon>
                    Link New Face
                  </VBtn>
                </div>

                <VProgressLinear v-if="loadingFaces" color="primary" indeterminate class="mb-3" />

                <div v-else-if="linkedFaces.length > 0">
                  <VChip
                    v-for="face in linkedFaces"
                    :key="face.faceUuid"
                    class="mr-2 mb-2"
                    color="primary"
                    variant="tonal"
                  >
                    <VIcon start size="small">mdi mdi-face-recognition</VIcon>
                    {{ face.faceUuid.slice(0, 8) }}...
                  </VChip>
                </div>
                <div v-else class="text-caption text-disabled">
                  No faces linked yet. Click "Link New Face" while customer is in front of camera.
                </div>
              </VCardText>
            </VCard>

            <VCard v-if="customer.facialRecognitionConsent" variant="outlined" color="error">
              <VCardText>
                <div class="font-weight-medium text-error mb-2">Withdraw Consent</div>
                <div class="text-body-2 mb-3">
                  If the customer wishes to withdraw their consent, all their facial data will be
                  permanently deleted. This action cannot be undone.
                </div>
                <VBtn
                  :loading="withdrawingConsent"
                  color="error"
                  variant="tonal"
                  @click="showWithdrawDialog = true"
                >
                  <VIcon start>mdi mdi-delete</VIcon>
                  Withdraw Consent & Delete Data
                </VBtn>
              </VCardText>
            </VCard>
          </VWindowItem>
        </VWindow>
      </VCardText>

      <VDialog v-model="showWithdrawDialog" max-width="450" persistent>
        <VCard>
          <VCardTitle class="text-error">
            <VIcon start color="error">mdi mdi-alert</VIcon>
            Confirm Consent Withdrawal
          </VCardTitle>
          <VCardText>
            <p class="mb-3">This will:</p>
            <ul class="mb-3">
              <li>Permanently delete all facial recognition data for this customer</li>
              <li>Remove all linked face records</li>
              <li>Disable automatic recognition for this customer</li>
            </ul>
            <p class="font-weight-medium">This action cannot be undone.</p>
          </VCardText>
          <VCardActions>
            <VSpacer />
            <VBtn @click="showWithdrawDialog = false">Cancel</VBtn>
            <VBtn
              :loading="withdrawingConsent"
              color="error"
              variant="flat"
              @click="withdrawConsent"
            >
              Confirm Withdrawal
            </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>

      <VDialog v-model="showLinkFaceDialog" max-width="600" persistent>
        <VCard>
          <VCardTitle>
            <VIcon start color="primary">mdi mdi-camera</VIcon>
            Link Face to Customer
          </VCardTitle>
          <VCardText>
            <VAlert color="info" variant="tonal" class="mb-4">
              <div class="text-body-2">
                Ask the customer to look at the Patronect camera. Select their face below
                to link it to their account.
              </div>
            </VAlert>

            <div v-if="loadingAvailableFaces" class="text-center py-4">
              <VProgressCircular indeterminate color="primary" size="48" />
              <div class="text-caption mt-2">Connecting to Patronect...</div>
            </div>

            <div v-else-if="availableFaces.length > 0" class="mb-4">
              <div class="text-subtitle-2 mb-2">Detected Faces ({{ availableFaces.length }})</div>
              <div class="d-flex flex-wrap ga-3">
                <VCard
                  v-for="face in availableFaces"
                  :key="face.faceUuid"
                  :color="detectedFace?.faceUuid === face.faceUuid ? 'primary' : undefined"
                  :variant="detectedFace?.faceUuid === face.faceUuid ? 'tonal' : 'outlined'"
                  class="pa-2 cursor-pointer"
                  style="width: 120px"
                  @click="selectFace(face)"
                >
                  <VImg
                    v-if="face.thumbnailFilename"
                    :src="getThumbnailUrl(face.thumbnailFilename)"
                    height="80"
                    cover
                    class="rounded mb-2"
                  />
                  <VIcon v-else size="48" class="d-block mx-auto mb-2">mdi mdi-face-recognition</VIcon>
                  <div class="text-caption text-center text-truncate">
                    {{ face.faceUuid?.slice(0, 8) }}...
                  </div>
                  <div class="text-caption text-center text-disabled">
                    {{ face.isNew ? 'New' : `Visit #${face.visitCount || 1}` }}
                  </div>
                </VCard>
              </div>
            </div>

            <div v-else class="text-center py-4">
              <VIcon size="48" color="grey">mdi mdi-face-recognition</VIcon>
              <div class="text-caption mt-2 text-disabled">
                No faces detected. Ask customer to face the camera.
              </div>
            </div>

            <VAlert v-if="detectedFace" color="success" variant="tonal" class="mt-3">
              <div class="font-weight-medium">Selected: {{ detectedFace.faceUuid?.slice(0, 12) }}...</div>
            </VAlert>
          </VCardText>
          <VCardActions>
            <VSpacer />
            <VBtn @click="cancelFaceLinking">Cancel</VBtn>
            <VBtn
              :disabled="!detectedFace"
              :loading="linkingFace"
              color="primary"
              variant="flat"
              @click="confirmLinkFace"
            >
              Link Selected Face
            </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>

      <VCardActions>
        <VSpacer/>
        <VBtn color="primary" @click="goToFullProfile">View Full Profile</VBtn>
        <VBtn @click="showDialog = false">Close</VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { DateTime } from 'luxon'
import { useApi } from '@shared/composables/useApi'
import { usePosEventBus } from '~/composables/usePosEventBus'
import { usePatronect } from '~/composables/usePatronect'

const { onViewCustomer, offViewCustomer } = usePosEventBus()
const { getCurrentFaces, getThumbnailUrl, isConfigured } = usePatronect()
const router = useRouter()

const showDialog = ref(false)
const customer = ref(null)
const recentOrders = ref([])
const visitHistory = ref([])
const loadingDetails = ref(false)
const loadingVisits = ref(false)
const activeTab = ref('overview')

const consentText = ref('')
const consentAcknowledged = ref(false)
const grantingConsent = ref(false)
const withdrawingConsent = ref(false)
const showWithdrawDialog = ref(false)
const linkedFaces = ref([])
const loadingFaces = ref(false)
const showLinkFaceDialog = ref(false)
const linkingFace = ref(false)
const detectedFace = ref(null)
const availableFaces = ref([])
const loadingAvailableFaces = ref(false)
let facePollingInterval = null

const handleViewCustomer = async (customerData) => {
  customer.value = customerData
  showDialog.value = true
  recentOrders.value = []
  visitHistory.value = []
  linkedFaces.value = []
  consentAcknowledged.value = false
  activeTab.value = 'overview'

  if (customerData.id) {
    loadRecentOrders(customerData.id)
    loadVisitHistory(customerData.id)
    loadConsentText()
    if (customerData.facialRecognitionConsent) {
      loadLinkedFaces(customerData.id)
    }
  }
}

const loadConsentText = async () => {
  try {
    const data = await useApi('/api/patronect/consent-text', null, { method: 'GET' }, 2)
    consentText.value = data?.consentText || ''
  } catch (error) {
    console.error('Failed to load consent text:', error)
    consentText.value = 'Unable to load consent text. Please try again.'
  }
}

const loadLinkedFaces = async (customerId) => {
  loadingFaces.value = true
  try {
    const data = await useApi(`/api/customers/${customerId}/linked-faces`, null, { method: 'GET' }, 2)
    linkedFaces.value = data?.faces || []
  } catch (error) {
    console.error('Failed to load linked faces:', error)
    linkedFaces.value = []
  } finally {
    loadingFaces.value = false
  }
}

const grantConsent = async () => {
  if (!customer.value?.id || !consentAcknowledged.value) return

  grantingConsent.value = true
  try {
    const data = await useApi(
      `/api/patronect/customers/${customer.value.id}/facial-consent`,
      { method: 'verbal_pos' },
      { method: 'POST' },
      2
    )
    if (data?.success) {
      customer.value.facialRecognitionConsent = true
      customer.value.facialRecognitionConsentAt = data.consentedAt
      customer.value.facialRecognitionConsentMethod = data.method
    }
  } catch (error) {
    console.error('Failed to grant consent:', error)
  } finally {
    grantingConsent.value = false
  }
}

const withdrawConsent = async () => {
  if (!customer.value?.id) return

  withdrawingConsent.value = true
  try {
    const data = await useApi(
      `/api/patronect/customers/${customer.value.id}/facial-consent`,
      null,
      { method: 'DELETE' },
      2
    )
    if (data?.success) {
      customer.value.facialRecognitionConsent = false
      customer.value.facialRecognitionConsentWithdrawnAt = data.withdrawnAt
      linkedFaces.value = []
      showWithdrawDialog.value = false
    }
  } catch (error) {
    console.error('Failed to withdraw consent:', error)
  } finally {
    withdrawingConsent.value = false
  }
}

const startFaceLinking = async () => {
  detectedFace.value = null
  availableFaces.value = []
  showLinkFaceDialog.value = true

  if (!isConfigured.value) {
    console.warn('[CustomerDetails] Patronect not configured')
    return
  }

  loadingAvailableFaces.value = true
  await pollFaces()
  loadingAvailableFaces.value = false

  facePollingInterval = setInterval(pollFaces, 2000)
}

const pollFaces = async () => {
  try {
    const faces = await getCurrentFaces()
    availableFaces.value = faces || []
    if (faces && faces.length === 1 && !detectedFace.value) {
      detectedFace.value = faces[0]
    }
  } catch (err) {
    console.error('[CustomerDetails] Failed to poll faces:', err)
  }
}

const selectFace = (face) => {
  detectedFace.value = face
}

const cancelFaceLinking = () => {
  showLinkFaceDialog.value = false
  detectedFace.value = null
  availableFaces.value = []
  if (facePollingInterval) {
    clearInterval(facePollingInterval)
    facePollingInterval = null
  }
}

const confirmLinkFace = async () => {
  if (!detectedFace.value?.faceUuid || !customer.value?.id) return

  linkingFace.value = true
  try {
    const data = await useApi(
      `/api/patronect/faces/${detectedFace.value.faceUuid}/link-customer`,
      { customerId: customer.value.id },
      { method: 'POST' },
      2
    )
    if (data?.success) {
      await loadLinkedFaces(customer.value.id)
      showLinkFaceDialog.value = false
      detectedFace.value = null
      availableFaces.value = []
    }
  } catch (error) {
    console.error('Failed to link face:', error)
  } finally {
    linkingFace.value = false
    if (facePollingInterval) {
      clearInterval(facePollingInterval)
      facePollingInterval = null
    }
  }
}

const loadRecentOrders = async (customerId) => {
  loadingDetails.value = true
  try {
    const data = await useApi(`/api/customers/${customerId}/details`, null, { method: 'GET' }, 2)
    recentOrders.value = data?.recentOrders || []
  } catch (error) {
    console.error('Failed to load customer orders:', error)
    recentOrders.value = []
  } finally {
    loadingDetails.value = false
  }
}

const loadVisitHistory = async (customerId) => {
  loadingVisits.value = true
  try {
    const data = await useApi(`/api/customers/${customerId}/visits`, null, { method: 'GET' }, 2)
    visitHistory.value = data?.visits || []
  } catch (error) {
    console.error('Failed to load visit history:', error)
    visitHistory.value = []
  } finally {
    loadingVisits.value = false
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A'
  try {
    return DateTime.fromISO(dateStr).toFormat('dd MMM yyyy')
  } catch {
    return 'N/A'
  }
}

const formatVisitDate = (dateStr) => {
  if (!dateStr) return 'N/A'
  try {
    const dt = DateTime.fromISO(dateStr)
    const now = DateTime.now()
    if (dt.hasSame(now, 'day')) {
      return `Today ${dt.toFormat('h:mm a')}`
    }
    if (dt.hasSame(now.minus({ days: 1 }), 'day')) {
      return `Yesterday ${dt.toFormat('h:mm a')}`
    }
    return dt.toFormat('dd MMM yyyy h:mm a')
  } catch {
    return 'N/A'
  }
}

const getTierColor = (cust) => {
  const spend = Number(cust?.totalSpend || 0)
  if (spend >= 1000) return 'amber'
  if (spend >= 500) return 'grey'
  return 'brown'
}

const getTierIcon = (cust) => {
  const spend = Number(cust?.totalSpend || 0)
  if (spend >= 1000) return 'mdi-crown'
  if (spend >= 500) return 'mdi-medal'
  return 'mdi-star'
}

const getTierLabel = (cust) => {
  const spend = Number(cust?.totalSpend || 0)
  if (spend >= 1000) return 'Gold'
  if (spend >= 500) return 'Silver'
  return 'Bronze'
}

const goToFullProfile = () => {
  showDialog.value = false
  router.push({ path: '/customers', query: { search: customer.value?.phone || customer.value?.email } })
}

onMounted(() => {
  onViewCustomer(handleViewCustomer)
})

onUnmounted(() => {
  offViewCustomer(handleViewCustomer)
  if (facePollingInterval) {
    clearInterval(facePollingInterval)
    facePollingInterval = null
  }
})
</script>
